import { Button } from '@tempo-io/sui-button';
import { CardAction, CardContent, CardHeader } from '@tempo-io/sui-card';
import { cn } from '@tempo-io/sui-class-utils';
import { CalendarToday, OpenInFull } from '@tempo-io/sui-icons';
import { Input } from '@tempo-io/sui-input';
import { Label } from '@tempo-io/sui-label';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import type {
  ChangeEvent,
  ComponentProps,
  JSXElementConstructor,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwimlaneEventPublisher } from '@/common/components/ui/swimlane/api';
import { useSwimlaneRootProps } from '@/common/components/ui/swimlane/context';
import type { IHeader } from '@/common/components/ui/swimlane/types';
import { IMPLICIT_ROW_ID } from '../../constants';
import { StyledCard } from '../cards/SelectableCard';

type OnDateRangeChange = (dates: [string | undefined, string | undefined]) => void;
type DateSelectorProps = {
  onChange: OnDateRangeChange;
};
export type DateSelectorComponent = JSXElementConstructor<DateSelectorProps>;

export type AddCardFormProps = {
  columnHeader: IHeader;
  rowHeader?: IHeader;
  onClose: () => void;
  index: number;
  isInvalid?: boolean;
  slots?: {
    dateSelector?: DateSelectorComponent;
  };
  slotProps?: {
    dateSelector?: DateSelectorProps;
  };
} & ComponentProps<typeof StyledCard>;

export function AddCardForm({
  onClose,
  index,
  columnHeader,
  rowHeader,
  isInvalid = false,
  slots = {},
  slotProps = {},
  ...containerProps
}: AddCardFormProps) {
  const { isCompact } = useSwimlaneRootProps();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [dates, setDates] = useState<(string | undefined)[]>([]);
  const [title, setTitle] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const publishEvent = useSwimlaneEventPublisher();

  const SlotDateSelector = slots.dateSelector;
  const dateSelectorSlotProps = slotProps.dateSelector ?? {};

  const onDateRangeChange: OnDateRangeChange = ([start, end]) => {
    setDates([start, end]);
    if (start && end) {
      setShowDatePicker(false);
    }
  };

  const onTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const addCard = useCallback(
    (mode: 'simple' | 'advanced') => {
      publishEvent('addCard', {
        title,
        startDate: dates[0],
        endDate: dates[1],
        rowID: rowHeader?.id ?? IMPLICIT_ROW_ID,
        columnID: columnHeader.id,
        index,
        mode,
      });
    },
    [publishEvent, title, dates, rowHeader, columnHeader, index],
  );

  const addCardRef = useRef(addCard);
  addCardRef.current = addCard;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleUserKeyPress = useCallback((event: ReactKeyboardEvent | KeyboardEvent) => {
    event.stopPropagation();

    if (event.key === 'Enter') {
      addCardRef.current('simple');
      onCloseRef.current();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const toggleDatePicker = () => setShowDatePicker(!showDatePicker);

  const titleRef = useRef(title);
  titleRef.current = title;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        if (titleRef.current) {
          addCardRef.current('simple');
        }
        onCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <StyledCard
      ref={cardRef}
      isCompact={isCompact}
      data-testid="add-card-form"
      borderColor="#1D1D1B"
      {...containerProps}
    >
      <CardHeader className="p-0">
        <div />
        <CardAction data-slot="card-action">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => {
              addCard('advanced');
              onClose();
            }}
            aria-label="Add item with advanced options"
          >
            <OpenInFull size={16} />
          </Button>
        </CardAction>
      </CardHeader>
      {/* COULDDO: Use DateRangePicker with the field slot: https://mui.com/x/react-date-pickers/custom-field/#using-a-button */}
      <CardContent className="px-0 py-1 fs-exclude">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <Label htmlFor="item-name" className="mb-1">
              Item Name
            </Label>
            <Input
              id="item-name"
              onKeyDown={handleUserKeyPress}
              onChange={onTitleChange}
              className="h-8"
            />
          </div>
          <div className="flex flex-col">
            <Label htmlFor="date-picker" className="mb-1">
              Date (Optional)
            </Label>
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  onClick={toggleDatePicker}
                  className={cn(
                    'w-full justify-start border-border-neutral py-[7px] px-1',
                    !dates.length && 'text-text-neutral-secondary',
                  )}
                  aria-label="Select Date Range"
                >
                  <CalendarToday size={20} className="text-text-neutral-secondary mr-0" />
                  {!!dates.length && (
                    <span className="mt-1.5 text-xs leading-3 font-normal text-text-neutral-secondary">
                      {`${dates[0]} - ${dates[1]}`}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-10" align="start">
                {SlotDateSelector && (
                  <SlotDateSelector onChange={onDateRangeChange} {...dateSelectorSlotProps} />
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardContent>

      <CardAction className="justify-end p-0">
        <Button size="sm" variant="ghost" className="text-text-neutral-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="default"
          disabled={isInvalid}
          onClick={() => {
            if (isInvalid) return;

            addCard('simple');
            onClose();
          }}
        >
          Create
        </Button>
      </CardAction>
    </StyledCard>
  );
}
